import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProductImporter from '../components/ProductImporter';
import { RootState, AppDispatch } from '../redux/store';
import { 
  setProducts, 
  deleteProductAsync, 
  updateProductAsync,
  addProductAsync,
  Product 
} from '../redux/slices/productSlice';
import * as api from '../services/api';
import * as mockDataService from '../services/mockData';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { NOTIFICATION_TYPES } from '../utils/notificationUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Material UI imports
import {
  Container, Typography, Grid, Card, CardContent, CardMedia, Button,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions,
  Box, CircularProgress, CardActionArea, CardActions, Chip, IconButton,
  Tooltip, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem,
  Paper, Tabs, Tab, Switch, FormControlLabel, Zoom, InputAdornment,
  Divider, Avatar, Badge, Pagination
} from '@mui/material';

// Icons
import {
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Videocam as VideocamIcon,
  AutoFixHigh as AutoFixHighIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  ContentCopy as ContentCopyIcon,
  Star as StarIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

// Função para gerar ID único
const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Estendendo a interface Product para incluir os campos adicionais que queremos
interface ExtendedProduct extends Product {
  tags?: string[];
  featured?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ProductFormData {
  type: 'produto' | 'servico';
  name: string;
  description: string;
  price: string;
  imageUrl: string;
  videoUrl?: string;
  category?: string;
  tags?: string[];
  featured?: boolean;
}

const ProductPagePrototype: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const products = useSelector((state: RootState) => state.products.items);
  const createNotification = useCreateNotification();
  
  // Estados
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [formData, setFormData] = useState<ProductFormData>({
    type: 'produto',
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    category: '',
    tags: [],
    featured: false
  });
  
  const [editFormData, setEditFormData] = useState<ProductFormData>({
    type: 'produto',
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    category: '',
    tags: [],
    featured: false
  });
  
  const [isImprovingText, setIsImprovingText] = useState(false);

  // Efeito para carregar produtos
  useEffect(() => {
    fetchProducts();
  }, [dispatch]);

  // Função para buscar produtos
  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Carregar produtos do MongoDB via API
      const apiResponse = await api.getProducts();
      const apiProducts = apiResponse.data;
      
      // Carregar produtos do localStorage
      const localProducts = await mockDataService.getProducts();
      
      // Mesclar produtos, evitando duplicatas por ID
      const mergedProducts: Product[] = [...apiProducts];
      
      // Adicionar produtos do localStorage que não existem na API
      localProducts.forEach((localProduct: Product) => {
        if (!mergedProducts.some(p => p.id === localProduct.id)) {
          mergedProducts.push(localProduct);
        }
      });
      
      dispatch(setProducts(mergedProducts));
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      setSnackbarMessage("Erro ao carregar produtos. Tente novamente.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para formulários e ações
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name as string]: value });
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name as string]: value });
  };

  // Função para lidar com mudança de imagem
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      
      // Criar preview da imagem
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target) {
          const dataUrl = event.target.result as string;
          setPreviewImage(dataUrl);
          setFormData({ ...formData, imageUrl: dataUrl });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para lidar com mudança de vídeo
  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedVideo(file);
      
      // Criar preview do vídeo
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target) {
          const dataUrl = event.target.result as string;
          setPreviewVideo(dataUrl);
          setFormData({ ...formData, videoUrl: dataUrl });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para gerar o próximo código disponível
  const generateNextCode = (type: 'produto' | 'servico'): string => {
    const prefix = type === 'produto' ? 'P' : 'S';
    const existingCodes = products
      .filter(p => p.type === type)
      .map(p => p.code || '')
      .filter(code => code.startsWith(prefix))
      .map(code => parseInt(code.substring(1), 10))
      .filter(num => !isNaN(num));
    
    const nextNumber = existingCodes.length > 0 ? Math.max(...existingCodes) + 1 : 1;
    return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
  };

  // Função para adicionar um produto
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Criando o produto com apenas os campos suportados pela interface Product
      const newProduct: Product = {
        id: generateUniqueId(),
        code: generateNextCode(formData.type),
        type: formData.type,
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        imageUrl: formData.imageUrl,
        videoUrl: formData.videoUrl,
        category: formData.category
      };
      
      // Armazenando os campos extras para uso futuro, se necessário
      const extendedData = {
        tags: formData.tags,
        featured: formData.featured,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await dispatch(addProductAsync(newProduct));
      
      setOpenDialog(false);
      resetForm();
      
      createNotification({
        title: 'Sucesso',
        message: `${formData.type === 'produto' ? 'Produto' : 'Serviço'} adicionado com sucesso!`,
        type: 'success'
      });
    } catch (error) {
      console.error("Erro ao adicionar produto:", error);
      setSnackbarMessage("Erro ao adicionar produto. Tente novamente.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Função para resetar o formulário
  const resetForm = () => {
    setFormData({
      type: 'produto',
      name: '',
      description: '',
      price: '',
      imageUrl: '',
      category: '',
      tags: [],
      featured: false
    });
    setSelectedImage(null);
    setPreviewImage(null);
    setSelectedVideo(null);
    setPreviewVideo(null);
  };

  // Função para editar um produto
  const handleEditClick = (productId: string) => {
    console.log('Editando produto:', productId);
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProductId(productId);
      setEditFormData({
        type: product.type || 'produto',
        name: product.name || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        imageUrl: product.imageUrl || '',
        videoUrl: product.videoUrl || '',
        category: product.category || '',
        tags: [],
        featured: false
      });
      setPreviewImage(product.imageUrl || null);
      setPreviewVideo(product.videoUrl || null);
      setOpenEditDialog(true);
    } else {
      console.error('Produto não encontrado:', productId);
    }
  };

  // Função para salvar edições
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProductId) return;
    
    try {
      // Criando o produto atualizado com apenas os campos suportados pela interface Product
      const updatedProduct: Product = {
        id: selectedProductId,
        code: products.find(p => p.id === selectedProductId)?.code || generateNextCode(editFormData.type),
        type: editFormData.type,
        name: editFormData.name,
        description: editFormData.description,
        price: parseFloat(editFormData.price),
        imageUrl: editFormData.imageUrl,
        videoUrl: editFormData.videoUrl,
        category: editFormData.category
      };
      
      // Armazenando os campos extras para uso futuro, se necessário
      const extendedData = {
        tags: editFormData.tags,
        featured: editFormData.featured,
        updatedAt: new Date().toISOString()
      };
      
      await dispatch(updateProductAsync(updatedProduct));
      
      setOpenEditDialog(false);
      setSelectedProductId(null);
      
      createNotification({
        title: 'Sucesso',
        message: `${editFormData.type === 'produto' ? 'Produto' : 'Serviço'} atualizado com sucesso!`,
        type: 'success'
      });
    } catch (error) {
      console.error("Erro ao atualizar produto:", error);
      setSnackbarMessage("Erro ao atualizar produto. Tente novamente.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Função para excluir um produto
  const handleDeleteClick = (productId: string) => {
    setSelectedProductId(productId);
    setOpenDeleteDialog(true);
  };

  // Função para confirmar exclusão
  const handleConfirmDelete = async () => {
    if (!selectedProductId) return;
    
    try {
      await dispatch(deleteProductAsync(selectedProductId));
      setOpenDeleteDialog(false);
      setSelectedProductId(null);
      
      createNotification({
        title: 'Sucesso',
        message: "Item excluído com sucesso!",
        type: 'success'
      });
    } catch (error) {
      console.error("Erro ao excluir produto:", error);
      setSnackbarMessage("Erro ao excluir produto. Tente novamente.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Função para fechar snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Função para exportar produtos para Excel
  const exportProductsToExcel = () => {
    try {
      // Preparar dados para exportação
      const exportData = products.map(product => ({
        Código: product.code || '',
        Tipo: product.type === 'produto' ? 'Produto' : 'Serviço',
        Nome: product.name || '',
        Descrição: product.description || '',
        Preço: product.price || 0,
        Categoria: product.category || '',
        'Data de Criação': new Date().toLocaleDateString(),
        'Última Atualização': new Date().toLocaleDateString(),
      }));
      
      // Criar workbook
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Produtos");
      
      // Gerar arquivo Excel
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Salvar arquivo
      saveAs(data, `produtos_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setSnackbarMessage("Exportação concluída com sucesso!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Erro ao exportar produtos:", error);
      setSnackbarMessage("Erro ao exportar produtos. Tente novamente.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Função para melhorar texto com IA
  const handleImproveTextWithAI = async () => {
    try {
      setIsImprovingText(true);
      
      // Simular chamada de API para IA (substitua por chamada real)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const improvedText = `${formData.description}\n\nEste produto de alta qualidade oferece excelente custo-benefício e atende perfeitamente às necessidades do mercado imobiliário atual. Ideal para profissionais que buscam eficiência e resultados.`;
      
      setFormData({ ...formData, description: improvedText });
      
      setSnackbarMessage("Descrição aprimorada com sucesso!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Erro ao melhorar texto:", error);
      setSnackbarMessage("Erro ao melhorar texto. Tente novamente.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsImprovingText(false);
    }
  };

  // Função para duplicar um produto
  const handleDuplicateProduct = async (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      try {
        // Criar um novo produto com base no original, mas com ID e código novos
        const duplicatedProduct: Product = {
          id: generateUniqueId(),
          code: generateNextCode(product.type),
          type: product.type,
          name: `${product.name} (Cópia)`,
          description: product.description,
          price: product.price,
          imageUrl: product.imageUrl,
          videoUrl: product.videoUrl,
          category: product.category
        };
        
        // Persistir o produto duplicado usando a ação assíncrona
        await dispatch(addProductAsync(duplicatedProduct));
        
        createNotification({
          title: 'Sucesso',
          message: `${product.type === 'produto' ? 'Produto' : 'Serviço'} duplicado com sucesso!`,
          type: 'success'
        });
      } catch (error) {
        console.error("Erro ao duplicar produto:", error);
        setSnackbarMessage("Erro ao duplicar produto. Tente novamente.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  // Função para alternar visualização
  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  // Função para filtrar produtos
  const getFilteredProducts = () => {
    return products.filter(product => {
      // Filtrar por pesquisa
      const matchesSearch = searchQuery === '' || 
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.code?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filtrar por categoria
      const matchesCategory = filterCategory === '' || product.category === filterCategory;
      
      // Filtrar por tipo
      const matchesType = filterType === '' || product.type === filterType;
      
      return matchesSearch && matchesCategory && matchesType;
    });
  };

  // Categorias disponíveis (extraídas dos produtos)
  const categoriesSet = new Set<string>();
  products.forEach(p => {
    if (p.category) categoriesSet.add(p.category);
  });
  const availableCategories = Array.from(categoriesSet);
  
  // Renderizar produtos filtrados
  const filteredProducts = getFilteredProducts();
  
  // Lógica de paginação
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Função para mudar de página
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    // Rolar para o topo quando mudar de página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Renderizar componente
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ 
          fontWeight: 'bold', 
          color: 'primary.main',
          letterSpacing: '0.5px',
          padding: '8px 0'
        }}>
          Produtos e Serviços
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
              background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              transition: 'all 0.3s',
              '&:hover': { 
                transform: 'translateY(-3px)', 
                boxShadow: '0 6px 15px rgba(76, 175, 80, 0.4)',
                background: 'linear-gradient(45deg, #43A047 30%, #7CB342 90%)'
              }
            }}
          >
            Adicionar Produto
          </Button>
          
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setOpenImportDialog(true)}
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
              background: 'linear-gradient(45deg, #2196F3 30%, #03A9F4 90%)',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              transition: 'all 0.3s',
              '&:hover': { 
                transform: 'translateY(-3px)', 
                boxShadow: '0 6px 15px rgba(33, 150, 243, 0.4)',
                background: 'linear-gradient(45deg, #1E88E5 30%, #039BE5 90%)'
              }
            }}
          >
            Importar
          </Button>

          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={exportProductsToExcel}
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 4px 10px rgba(156, 39, 176, 0.3)',
              background: 'linear-gradient(45deg, #9C27B0 30%, #E040FB 90%)',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              transition: 'all 0.3s',
              '&:hover': { 
                transform: 'translateY(-3px)', 
                boxShadow: '0 6px 15px rgba(156, 39, 176, 0.4)',
                background: 'linear-gradient(45deg, #8E24AA 30%, #D500F9 90%)'
              }
            }}
          >
            Exportar
          </Button>
        </Box>
      </Box>
      
      {/* Barra de filtros */}
      <Paper elevation={3} sx={{ mb: 4, p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            placeholder="Buscar produtos..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="category-filter-label">Categoria</InputLabel>
              <Select
                labelId="category-filter-label"
                value={filterCategory}
                label="Categoria"
                onChange={(e) => setFilterCategory(e.target.value as string)}
              >
                <MenuItem value="">Todas</MenuItem>
                {availableCategories.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="type-filter-label">Tipo</InputLabel>
              <Select
                labelId="type-filter-label"
                value={filterType}
                label="Tipo"
                onChange={(e) => setFilterType(e.target.value as string)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="produto">Produtos</MenuItem>
                <MenuItem value="servico">Serviços</MenuItem>
              </Select>
            </FormControl>
            
            <Tooltip title={viewMode === 'grid' ? 'Visualização em Lista' : 'Visualização em Grid'}>
              <IconButton onClick={toggleViewMode}>
                {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
      
      {/* A paginação foi movida para o final da renderização dos produtos */}
      
      {/* Loading state */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : filteredProducts.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="textSecondary">
            Nenhum produto encontrado
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Tente ajustar os filtros ou adicionar novos produtos
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setOpenDialog(true)}
            sx={{ 
              mt: 3,
              borderRadius: 2,
              boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
              background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              transition: 'all 0.3s',
              '&:hover': { 
                transform: 'translateY(-3px)', 
                boxShadow: '0 6px 15px rgba(76, 175, 80, 0.4)',
                background: 'linear-gradient(45deg, #43A047 30%, #7CB342 90%)'
              }
            }}
          >
            Adicionar Produto
          </Button>
        </Paper>
      ) : (
        <>
          {/* Contador de resultados */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Exibindo {Math.min(currentPage * itemsPerPage, filteredProducts.length)} de {filteredProducts.length} produtos filtrados (total: {products.length})
            </Typography>
            {totalPages > 1 && (
              <Typography variant="body2" color="textSecondary">
                Página {currentPage} de {totalPages}
              </Typography>
            )}
          </Box>
          
          {/* Grid de produtos */}
          {viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {paginatedProducts.map((product) => (
                <Zoom in={true} key={product.id} style={{ transitionDelay: '100ms' }}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: 6
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="180"
                          image={product.imageUrl || '/placeholder-image.jpg'}
                          alt={product.name}
                          sx={{ objectFit: 'cover' }}
                        />
                        {/* Verificamos se o produto tem a propriedade featured usando uma abordagem segura */}
                        {(product as any).featured && (
                          <Chip
                            icon={<StarIcon fontSize="small" />}
                            label="Destaque"
                            color="primary"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                        <Chip
                          label={product.type === 'produto' ? 'Produto' : 'Serviço'}
                          color={product.type === 'produto' ? 'primary' : 'secondary'}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                      
                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {product.code}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {product.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                            {product.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </Typography>
                          {product.category && (
                            <Chip 
                              label={product.category} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </CardContent>
                      
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleEditClick(product.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicar">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleDuplicateProduct(product.id)}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteClick(product.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                </Zoom>
              ))}
            </Grid>
          ) : (
            /* Visualização em lista */
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {paginatedProducts.map((product, index) => (
                <React.Fragment key={product.id}>
                  {index > 0 && <Divider />}
                  <Box sx={{ 
                    p: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 2,
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}>
                    <Box sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden' }}>
                      <img 
                        src={product.imageUrl || '/placeholder-image.jpg'} 
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {product.name}
                          {/* Verificamos se o produto tem a propriedade featured usando uma abordagem segura */}
                        {(product as any).featured && (
                            <StarIcon sx={{ ml: 1, color: 'primary.main', fontSize: '0.8rem', verticalAlign: 'text-top' }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {product.code}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {product.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                        <Chip 
                          label={product.type === 'produto' ? 'Produto' : 'Serviço'} 
                          color={product.type === 'produto' ? 'primary' : 'secondary'}
                          size="small" 
                        />
                        {product.category && (
                          <Chip 
                            label={product.category} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                    
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', minWidth: 100, textAlign: 'right' }}>
                      {product.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(product.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="info" onClick={() => handleDuplicateProduct(product.id)}>
                        <ContentCopyIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(product.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </React.Fragment>
              ))}
            </Paper>
          )}
          
          {/* Controles de paginação */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={handlePageChange} 
              color="primary" 
              size="large"
              showFirstButton 
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  padding: '12px',
                },
                '& .Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            />
            <Typography variant="body2" color="textSecondary">
              Página {currentPage} de {totalPages} • Total de {filteredProducts.length} itens
            </Typography>
          </Box>
        </>
      )}
      
      {/* Diálogos */}
      {/* Diálogo de adição de produto */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {formData.type === 'produto' ? 'Adicionar Produto' : 'Adicionar Serviço'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="product-type-label">Tipo</InputLabel>
                  <Select
                    labelId="product-type-label"
                    name="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'produto' | 'servico' })}
                    label="Tipo"
                  >
                    <MenuItem value="produto">Produto</MenuItem>
                    <MenuItem value="servico">Serviço</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nome"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Descrição"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    onClick={handleImproveTextWithAI}
                    disabled={isImprovingText || !formData.description}
                    sx={{ ml: 'auto' }}
                  >
                    {isImprovingText ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Melhorando...
                      </>
                    ) : (
                      'Melhorar com IA'
                    )}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="price"
                  label="Preço"
                  value={formData.price}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="normal"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="product-category-label">Categoria</InputLabel>
                  <Select
                    labelId="product-category-label"
                    name="category"
                    value={formData.category || ''}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    label="Categoria"
                  >
                    <MenuItem value="">Nenhuma</MenuItem>
                    {availableCategories.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Imagem do Produto</Typography>
                  <input
                    accept="image/*"
                    id="product-image-upload"
                    type="file"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="product-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      sx={{ mb: 2 }}
                    >
                      Selecionar Imagem
                    </Button>
                  </label>
                  
                  {previewImage && (
                    <Box sx={{ mt: 2, position: 'relative', width: 200, height: 200 }}>
                      <img 
                        src={previewImage} 
                        alt="Preview" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Vídeo do Produto (opcional)</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Opção 1: Upload de arquivo */}
                    <Box>
                      <input
                        accept="video/*"
                        id="product-video-upload"
                        type="file"
                        onChange={handleVideoChange}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="product-video-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<VideocamIcon />}
                          sx={{ mb: 2 }}
                        >
                          Selecionar Vídeo
                        </Button>
                      </label>
                    </Box>
                    
                    {/* Opção 2: Link do vídeo */}
                    <TextField
                      name="videoUrl"
                      label="Ou informe o link do vídeo"
                      value={formData.videoUrl || ''}
                      onChange={handleChange}
                      fullWidth
                      placeholder="https://exemplo.com/video.mp4"
                      helperText="Informe o link direto para o vídeo ou YouTube"
                    />
                  </Box>
                  
                  {previewVideo && (
                    <Box sx={{ mt: 2, position: 'relative', width: 200, height: 200 }}>
                      <video 
                        src={previewVideo} 
                        controls 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.featured || false}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      name="featured"
                      color="primary"
                    />
                  }
                  label="Produto em destaque"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de edição de produto */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editFormData.type === 'produto' ? 'Editar Produto' : 'Editar Serviço'}
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="edit-product-type-label">Tipo</InputLabel>
                  <Select
                    labelId="edit-product-type-label"
                    name="type"
                    value={editFormData.type}
                    onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value as 'produto' | 'servico' })}
                    label="Tipo"
                  >
                    <MenuItem value="produto">Produto</MenuItem>
                    <MenuItem value="servico">Serviço</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nome"
                  value={editFormData.name}
                  onChange={handleEditChange}
                  fullWidth
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Descrição"
                  value={editFormData.description}
                  onChange={handleEditChange}
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => {
                      setIsImprovingText(true);
                      setTimeout(() => {
                        const improvedText = `${editFormData.description}\n\nEste produto de alta qualidade oferece excelente custo-benefício e atende perfeitamente às necessidades do mercado imobiliário atual. Ideal para profissionais que buscam eficiência e resultados.`;
                        setEditFormData({ ...editFormData, description: improvedText });
                        setIsImprovingText(false);
                        setSnackbarMessage("Descrição aprimorada com sucesso!");
                        setSnackbarSeverity("success");
                        setSnackbarOpen(true);
                      }, 1500);
                    }}
                    disabled={isImprovingText || !editFormData.description}
                    sx={{ ml: 'auto' }}
                  >
                    {isImprovingText ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Melhorando...
                      </>
                    ) : (
                      'Melhorar com IA'
                    )}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="price"
                  label="Preço"
                  value={editFormData.price}
                  onChange={handleEditChange}
                  fullWidth
                  required
                  margin="normal"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="edit-product-category-label">Categoria</InputLabel>
                  <Select
                    labelId="edit-product-category-label"
                    name="category"
                    value={editFormData.category || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                    label="Categoria"
                  >
                    <MenuItem value="">Nenhuma</MenuItem>
                    {availableCategories.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Imagem do Produto</Typography>
                  <input
                    accept="image/*"
                    id="edit-product-image-upload"
                    type="file"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="edit-product-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      sx={{ mb: 2 }}
                    >
                      Selecionar Imagem
                    </Button>
                  </label>
                  
                  {previewImage && (
                    <Box sx={{ mt: 2, position: 'relative', width: 200, height: 200 }}>
                      <img 
                        src={previewImage} 
                        alt="Preview" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Vídeo do Produto (opcional)</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Opção 1: Upload de arquivo */}
                    <Box>
                      <input
                        accept="video/*"
                        id="edit-product-video-upload"
                        type="file"
                        onChange={handleVideoChange}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="edit-product-video-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<VideocamIcon />}
                          sx={{ mb: 2 }}
                        >
                          Selecionar Vídeo
                        </Button>
                      </label>
                    </Box>
                    
                    {/* Opção 2: Link do vídeo */}
                    <TextField
                      name="videoUrl"
                      label="Ou informe o link do vídeo"
                      value={editFormData.videoUrl || ''}
                      onChange={handleEditChange}
                      fullWidth
                      placeholder="https://exemplo.com/video.mp4"
                      helperText="Informe o link direto para o vídeo ou YouTube"
                    />
                  </Box>
                  
                  {previewVideo && (
                    <Box sx={{ mt: 2, position: 'relative', width: 200, height: 200 }}>
                      <video 
                        src={previewVideo} 
                        controls 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.featured || false}
                      onChange={(e) => setEditFormData({ ...editFormData, featured: e.target.checked })}
                      name="featured"
                      color="primary"
                    />
                  }
                  label="Produto em destaque"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de importação de produtos */}
      <ProductImporter 
        open={openImportDialog} 
        onClose={() => setOpenImportDialog(false)} 
      />

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductPagePrototype;
